import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import axios from 'axios';
import { API_URL } from '../config';


export interface Project {
  id: number;
  name: string;
  description?: string;
  github_repo?: string;
  jira_project_key?: string;
  jira_cloud_id?: string;
  is_active: boolean;
  user_role?: string;
  is_default: boolean;
}

interface ProjectContextType {
  projects: Project[];
  currentProject: Project | null;
  loading: boolean;
  selectProject: (projectId: number) => void;
  refreshProjects: () => Promise<void>;
  setAsDefault: (projectId: number) => Promise<void>;
  hasJira: boolean;
  /** Jira site base URL (e.g. https://org.atlassian.net) for browse/copy links */
  jiraUrl: string | null;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasJira, setHasJira] = useState<boolean>(false);
  const [jiraUrl, setJiraUrl] = useState<string | null>(null);
  
  // Generate tab ID for this browser tab (sessionStorage is tab-specific)
  const getTabId = () => {
    let tabId = sessionStorage.getItem('complic_tab_id');
    if (!tabId) {
      tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('complic_tab_id', tabId);
    }
    return tabId;
  };
  
  const tabId = getTabId();
  
  useEffect(() => {
    // Load projects on mount
    refreshProjects();
  }, []);
  
  const refreshProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/projects/`);
      setProjects(response.data);
      
      // Determine which project to select
      const savedProjectId = sessionStorage.getItem(`complic_selected_project_${tabId}`);
      
      if (savedProjectId && response.data.length > 0) {
        // Try to load saved project for this tab
        const savedProject = response.data.find((p: Project) => p.id === parseInt(savedProjectId));
        if (savedProject) {
          setCurrentProject(savedProject);
          // Load Jira status for saved project (with small delay to ensure token is available)
          setTimeout(() => loadJiraStatus(savedProject.id), 500);
        } else {
          // Saved project not found, use default or first
          selectDefaultOrFirst(response.data);
        }
      } else {
        // No saved project, use default or first
        selectDefaultOrFirst(response.data);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      setProjects([]);
      setCurrentProject(null);
    } finally {
      setLoading(false);
    }
  };
  
  const selectDefaultOrFirst = (projectList: Project[]) => {
    if (projectList.length === 0) {
      setCurrentProject(null);
      return;
    }

    // Find default project
    const defaultProject = projectList.find(p => p.is_default);
    if (defaultProject) {
      setCurrentProject(defaultProject);
      sessionStorage.setItem(`complic_selected_project_${tabId}`, defaultProject.id.toString());
      // Load Jira status for initially selected project (with small delay to ensure token is available)
      setTimeout(() => loadJiraStatus(defaultProject.id), 500);
    } else {
      // Use first project
      setCurrentProject(projectList[0]);
      sessionStorage.setItem(`complic_selected_project_${tabId}`, projectList[0].id.toString());
      // Load Jira status for initially selected project (with small delay to ensure token is available)
      setTimeout(() => loadJiraStatus(projectList[0].id), 500);
    }
  };
  
  const loadJiraStatus = async (projectId: number) => {
    try {
      const token = localStorage.getItem('access_token');
      console.log(`[ProjectContext] Loading Jira status for project ${projectId}, has token: ${!!token}`);

      const response = await axios.get(`${API_URL}/api/projects/${projectId}/jira/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      console.log(`[ProjectContext] Jira status response:`, response.data);
      setHasJira(response.data.configured);
      setJiraUrl(response.data.jira_url || null);
    } catch (error: any) {
      console.error('Failed to load Jira status:', error);
      console.error('Error details:', error.response?.status, error.response?.data);
      setHasJira(false);
      setJiraUrl(null);
    }
  };

  const selectProject = (projectId: number) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setCurrentProject(project);
      // Save to sessionStorage (tab-specific)
      sessionStorage.setItem(`complic_selected_project_${tabId}`, projectId.toString());
      console.log(`[Tab ${tabId}] Selected project:`, project.name, `(workspace: ${project.github_repo})`);

      // Load Jira status for this project (with small delay to ensure context is ready)
      setTimeout(() => loadJiraStatus(projectId), 200);

      // Note: CodePanel now automatically switches to the new project's workspace
      // via useEffect watching currentProject.github_repo
    }
  };
  
  const setAsDefault = async (projectId: number) => {
    try {
      await axios.put(`${API_URL}/api/projects/set-default/${projectId}`);
      
      // Refresh projects to update is_default flags
      await refreshProjects();
      
      return Promise.resolve();
    } catch (error: any) {
      console.error('Failed to set default project:', error);
      return Promise.reject(error);
    }
  };
  
  // Debug: log hasJira whenever it changes
  React.useEffect(() => {
    console.log(`[ProjectContext] hasJira changed to:`, hasJira, `for project:`, currentProject?.name);
  }, [hasJira, currentProject]);

  return (
    <ProjectContext.Provider value={{
      projects,
      currentProject,
      loading,
      selectProject,
      refreshProjects,
      setAsDefault,
      hasJira,
      jiraUrl,
    }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within ProjectProvider');
  }
  return context;
};

export default ProjectContext;
